<template>
  <a-spin :spinning="!rights">
    <div
      v-if="packageList?.length && rights"
      class="w-full grid place-items-center rounded-[12px] bg-back-1"
    >
      <div
        v-if="order.status == undefined"
        :class="{
          'w-full': true,
          flex: true,
          'flex-col': isGlobalVersion(),
        }"
      >
        <StorageInfo
          v-if="rights"
          v-model:count="count"
          v-model:currentPackage="data.currentPackage"
          :feature-type="feature_type"
          :rights-name="rights.rights_name"
          :packageList="packageList"
        />
        <template v-if="!isGlobalVersion()">
          <div class="w-[1px] bg-[#f1f1f1]"></div>
          <PayQrcode
            v-model:pay-type="payType"
            v-model:agreement-checked="agreementChecked"
            :loading="creating"
            :qrcode="qrcode"
            :behavior="behavior"
            :price-text="total"
          >
            <Consultant
              class="mt-[20px]"
              :rightType="rights.rights_type"
              @transfer="onTransferClick"
            />
          </PayQrcode>
        </template>
        <AirwallexOperation
          v-else
          mode="payment"
          :hostPaymentPage="hostPaymentPage"
          :loading="creating || !hostPaymentPage"
          @payed="handlePayed"
        >
        </AirwallexOperation>
      </div>
      <div v-else-if="paySucc" class="flex-y-center w-full h-full">
        <PaySucc
          class="flex-1 py-[173px]"
          :title="paySuccTitle"
          :desc="paySuccDesc"
        />
        <div class="w-[1px] h-full bg-[#f1f1f1]"></div>
        <PaySuccTip class="max-w-[254px]"></PaySuccTip>
      </div>
      <PayFail v-else class="py-[173px]" @back="onRePay" />
    </div>
    <div v-else class="flex flex-y-center w-full h-[565px]"></div>
  </a-spin>
</template>

<script setup lang="ts" name="BuyRights">
import type { HostPaymentPage } from 'airwallex-payment-elements'
import useOrder, { useRights } from '../../useOrder'
import PayQrcode from '../pay-qrcode.vue'
import Consultant from '../consultant.vue'
import PaySucc from '../pay-succ.vue'
import PaySuccTip from '../pay-succ-tip.vue'
import PayFail from '../pay-fail.vue'
import StorageInfo from './storage-info.vue'
import AirwallexOperation from '~/features/payment/AirwallexOperation.vue'
import type { UserInfo } from '~/api/interface'
import {
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  RIGHTS_FEATURE,
  USER_LEVEL_KEY,
} from '~/api/interface'
import { RightsFeature } from '~/constants/rights'

const props = defineProps<{
  user_info?: UserInfo
  feature_type: RIGHTS_FEATURE
  behavior: ORDER_BEHAVIOR
  order_type: ORDER_TYPE
}>()

const { $t } = useNuxtApp()

const feature = computed(() => RightsFeature()[props.feature_type])
const count = ref(1) // 服务数量

const { rights, getRights } = useRights(props?.user_info?.rights_type)

const userInfo = useUserInfo()
const addPrice = computed(
  () =>
    rights.value?.rights_by_time.find((v) => v.id === userInfo.value?.rights_id)
      ?.add_price
)

const data = ref<
  Partial<{
    behavior: ORDER_BEHAVIOR
    rights_type: USER_LEVEL_KEY
    order_type: ORDER_TYPE
    parent_order_id: number
    num: number
    currentPackage: any
  }>
>({
  behavior: props.behavior,
  rights_type: props?.user_info?.rights_type,
  order_type: props.order_type,
  parent_order_id: props.user_info?.current_order_id,
  num: count.value || 1,
})

watch(count, () => {
  data.value.num = count.value
})

export interface IStoragePackage {
  id: number
  description: string
  price: number
  oldPrice: number
  type: string
  isGB: boolean
  min: number
  max: number
}

const packageList = computed(() => {
  if (!addPrice.value || props?.user_info?.rights_type === undefined) {
    return []
  }

  const temp: IStoragePackage = {
    id: 0,
    description: '',
    price: addPrice.value?.add_storage,
    oldPrice: addPrice.value?.add_storage_original,
    type: 'GB',
    isGB: true,
    min: 1,
    max: 20000,
  }
  data.value.currentPackage = temp
  if (
    [USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE].includes(
      props?.user_info?.rights_type
    )
  ) {
    return [temp]
  }

  if (
    [
      USER_LEVEL_KEY.ORG_STANDARD,
      USER_LEVEL_KEY.ORG_FLAGSHIP,
      USER_LEVEL_KEY.TEAM_LIGHT,
    ].includes(props?.user_info?.rights_type)
  ) {
    return [
      temp,
      {
        id: 1,
        description: '1024GB',
        price: addPrice.value?.add_storage_t,
        oldPrice: addPrice.value?.add_storage_t_original,
        type: 'TB',
        isGB: false,
        min: 1,
        max: 2000,
      },
    ]
  }
})

const total = computed(() => {
  if (Number.isNaN(count.value)) {
    return data.value.currentPackage?.price
  }
  return data.value.currentPackage?.price * count.value
})
const yuanFormat = computed(() => formatPrice(total.value).string)

const paySuccTitle = computed(() => $t('jia-gou-cheng-gong'))
const paySuccDesc = computed(() => {
  let results: string = `${rights.value?.rights_name}，`
  if (count.value) {
    results += `${count.value}${
      data.value.currentPackage.type === 'GB' ? 'GB' : 'TB'
    }`
  }
  results += $t(
    'featurevaluename-jia-gou-cheng-gong-cheng-gong-zhi-fu-yuanformatvalue',
    [feature.value.name, yuanFormat.value]
  )
  return results
})

const {
  agreementChecked,
  payType,
  qrcode,
  order,
  paySucc,
  onRePay,
  createOrder,
  creating,
  rawOrder,
} = useOrder(data)

const onTransferClick = () => {
  createOrder(true)
}

const hostPaymentPage = computed(() => {
  if (!rawOrder.value) {
    return
  }

  const id = rawOrder.value.id

  const config: HostPaymentPage = {
    customer_id: rawOrder.value!.other_info.vendor_customer_id,
    currency: rawOrder.value!.currency,
    client_secret: rawOrder.value!.other_info.airwallex_client_secret,
    intent_id: rawOrder.value!.vendor_trade_no,
    successUrl: `${location.origin}/paymentResult?xmov_oid=${id}`,
    failUrl: `${location.origin}/paymentResult?xmov_oid=${id}`,
  }

  return config
})

const handlePayed = () => location.reload()

watch(
  () => paySucc.value,
  async (cur, before) => {
    if (cur !== before && cur) {
      await useUpdateUserAndAccounts()
    }
  }
)

onMounted(getRights)
</script>
